import { useEffect, useState } from 'react';
import { BUNGIE_ROOT } from './config';
import { bungie } from './bungie';
import { idbDelete, idbGet, idbKeys, idbSet } from './idb';
import { reloadIfStale } from './stale';
import { TABLE_FORMAT, type ItemDef, type Table, type TableName } from './tables';
import type { TableJob } from './manifest.worker';

/**
 * Bungie's game database in the browser. Each table is downloaded the first time a page needs it, slimmed down and
 * kept, so it is fetched once per Destiny update. Single definitions the lite tables lack (an item's screenshot or
 * perks) come one at a time from Bungie and are kept too, as the app does.
 */

// gear: Bungie's 3D gear database (newest first) and gear CDN, for the 3D views
interface ManifestInfo { version: string; paths: Record<string, string>; gear: { dbs: { version: number; path: string }[]; cdn?: Record<string, string> } }

let info: Promise<ManifestInfo> | null = null;
const tables = new Map<string, Promise<Table<any>>>();

/** The current database version and where each table lives (asked once per visit). */
export function manifestInfo(): Promise<ManifestInfo> {
  info ??= bungie<any>('/Destiny2/Manifest/').then((m) => {
    const version: string = m.version;
    // tables from older Destiny updates are no longer needed
    void idbKeys().then((keys) => keys.filter((k) => (k.startsWith('t:') && !k.startsWith(`t:${version}:${TABLE_FORMAT}:`)) || (k.startsWith('d:') && !k.startsWith(`d:${version}:`))).forEach(idbDelete));
    return { version, paths: m.jsonWorldComponentContentPaths.en, gear: { dbs: [...(m.mobileGearAssetDataBases ?? [])].sort((a, b) => b.version - a.version), cdn: m.mobileGearCDN } };
  }).catch((e) => { info = null; throw e; });
  return info;
}

/** One table, by hash. Downloaded and stored on first use. */
export function table<T = any>(name: TableName): Promise<Table<T>> {
  let t = tables.get(name);
  if (!t) {
    t = load(name).catch((e) => { tables.delete(name); throw e; });
    tables.set(name, t);
  }
  return t as Promise<Table<T>>;
}

async function load(name: TableName): Promise<Table<any>> {
  const { version, paths } = await manifestInfo();
  const key = `t:${version}:${TABLE_FORMAT}:${name}`;
  const kept = await idbGet<Table<any>>(key);
  if (kept) return kept;
  const path = paths[name];
  if (!path) return {};
  return new Promise((resolve, reject) => {
    const w = new Worker(new URL('./manifest.worker.ts', import.meta.url), { type: 'module' });   // (written out in full: Vite only builds a worker in this form)
    w.onmessage = (e) => {
      w.terminate();
      if (e.data.ok) resolve(e.data.table); else reject(new Error(e.data.error));
    };
    // its own file may be gone (a tab opened before the last deploy): then the page reloads
    w.onerror = (e) => { w.terminate(); void reloadIfStale(); reject(new Error(e.message || 'download failed')); };
    w.postMessage({ url: BUNGIE_ROOT + path, name, key } satisfies TableJob);
  });
}

/** Several tables at once. */
export async function tablesOf<K extends TableName>(...names: K[]): Promise<Record<K, Table<any>>> {
  const all = await Promise.all(names.map((n) => table(n)));
  return Object.fromEntries(names.map((n, i) => [n, all[i]])) as Record<K, Table<any>>;
}

export const items = () => table<ItemDef>('DestinyInventoryItemLiteDefinition');

// ---------------- single full definitions, one request each, kept ----------------

const singles = new Map<string, Promise<any>>();

/** One full definition from Bungie (for the fields the lite tables leave out). Null when Bungie has none. */
export function definition<T = any>(kind: string, hash: number | string): Promise<T | null> {
  const id = `${kind}/${hash}`;
  let p = singles.get(id);
  if (!p) {
    p = (async () => {
      const { version } = await manifestInfo();
      const key = `d:${version}:${id}`;
      const kept = await idbGet<T>(key);
      if (kept) return kept;
      try {
        const d = await bungie<T>(`/Destiny2/Manifest/${kind}/${hash}/`);
        void idbSet(key, d);
        return d;
      } catch {
        return null;
      }
    })();
    singles.set(id, p);
  }
  return p;
}

/** React: a table, or undefined while it loads. */
export function useTable<T = any>(name: TableName): Table<T> | undefined {
  const [t, setT] = useState<Table<T>>();
  useEffect(() => {
    let live = true;
    table<T>(name).then((v) => live && setT(v)).catch(() => live && setT({}));
    return () => { live = false; };
  }, [name]);
  return t;
}
